import { Grid } from '../../core/Grid.js';
import { Position, Point } from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';
import {
    DiagonalWallType,
    SectionId,
    SectionTag,
    Basin,
    Rational,
    cellKey,
    sectionKey,
    parseSectionKey,
    getSections,
    getQuadrants,
    getSectionSize,
    getSectionContaining,
    sectionContains,
    getNeighborSection,
    getExternalNeighbors,
    getHorizontalNeighbors,
    cycleWallType,
    wallTouchesBoundary,
    getAdjacentWallCells,
    wallsConnect,
    reduceRational,
    addRationals,
    subtractRationals,
    multiplyRationals,
    divideRational,
    compareRationals,
    rationalToNumber,
    colToLetter
} from './DelugeUtils.js';
import type { DropHint } from './DelugeUtils.js';
import { DelugeDebug } from './DelugeDebug.js';
import { stripSeparators } from '../stateSerialization.js';

/**
 * The cell a diagonal between two intersections cuts through, or null when they
 * aren't diagonal neighbours. The cell is the one whose top-left corner is the
 * upper-left of the pair.
 */
export function diagonalCellBetween(a: Point, b: Point): Position | null {
    if (Math.abs(a.row - b.row) !== 1 || Math.abs(a.col - b.col) !== 1) return null;
    return { row: Math.min(a.row, b.row), col: Math.min(a.col, b.col) };
}

/**
 * Which diagonal joins two diagonal-neighbour intersections: `'b'` (a backslash)
 * when they run top-left to bottom-right, `'f'` (a forward slash) otherwise.
 */
export function diagonalTypeBetween(a: Point, b: Point): 'f' | 'b' {
    return (b.row - a.row) === (b.col - a.col) ? 'b' : 'f';
}

export class DelugeGame {
    protected grid: Grid;
    protected diagonalWalls: Map<string, DiagonalWallType> = new Map();
    /** Keyboard trace anchor (a grid intersection); transient, never serialized. */
    protected traceAnchor: Point | null = null;
    protected faucetCol: number = 0;

    // Computed state - fill times for each section
    protected sectionFillTime: Map<string, number> = new Map();
    private digitPositions: Map<number, Position> = new Map();
    protected hasUnreachableSections: boolean = false;
    protected reachableSections: Set<string> = new Set();
    protected disconnectedWalls: Set<string> = new Set();
    // Spill points (water tipping over an edge), keyed by cell ("row,col"). At most
    // one section per cell can drop (the one holding the 'd' quadrant), so one hint
    // per cell suffices. Deduped across the many flow recomputations per simulation.
    protected dropHints: Map<string, DropHint> = new Map();

    // Debug helper
    protected debug: DelugeDebug = new DelugeDebug();

    // Convenience accessor for grid serializer
    protected get gs() { return this.grid.serializer; }

    constructor(grid: Grid) {
        this.grid = grid;
        this.initializeDigitPositions();
        this.recalculate();
    }

    private initializeDigitPositions(): void {
        const positions = this.grid.getAllDigitPositions();
        for (const [digit, pos] of positions) {
            this.digitPositions.set(digit, pos);
        }
    }

    getGrid(): Grid {
        return this.grid;
    }

    // Debug logging
    setDebugTimestamps(timestamps: number[]): void {
        this.debug.setTimestamps(timestamps);
    }

    logDebugSnapshots(): void {
        this.debug.logSnapshots();
    }

    getFaucetCol(): number {
        return this.faucetCol;
    }

    getReachableSections(): Set<string> {
        return this.reachableSections;
    }

    getDisconnectedWalls(): Set<string> {
        return this.disconnectedWalls;
    }

    getHasUnreachableSections(): boolean {
        return this.hasUnreachableSections;
    }

    getDiagonalWalls(): Map<string, DiagonalWallType> {
        return this.diagonalWalls;
    }

    hasWall(row: number, col: number): boolean {
        return this.diagonalWalls.has(cellKey(row, col));
    }

    // ── Keyboard trace: diagonals snapped between intersections ─────────────

    /** Whether a diagonal wall may occupy this cell — on the board, not a digit,
     *  not an active gate. */
    canPlaceWallAt(row: number, col: number): boolean {
        if (row < 0 || row >= this.grid.size || col < 0 || col >= this.grid.size) return false;
        if (this.grid.hasDigitAt({ row, col })) return false;
        return !this.isGateCellAt(row, col);
    }

    /**
     * Toggle **one** of a cell's two diagonals, leaving the other alone. So an empty
     * cell takes it, the opposite diagonal becomes a cross, tracing back over it takes
     * it away again, and removing one arm of a cross leaves the other standing.
     * Returns true if the board changed.
     */
    toggleDiagonal(row: number, col: number, type: 'f' | 'b'): boolean {
        if (!this.canPlaceWallAt(row, col)) return false;

        const key = cellKey(row, col);
        const current = this.diagonalWalls.get(key) ?? null;
        const other: 'f' | 'b' = type === 'f' ? 'b' : 'f';
        const hasType = current === type || current === 'x';
        const hasOther = current === other || current === 'x';

        const next: DiagonalWallType = hasType
            ? (hasOther ? other : null)   // take it away, leaving whatever else is there
            : (hasOther ? 'x' : type);    // add it alongside whatever is there
        if (current === next) return false;

        if (next === null) this.diagonalWalls.delete(key);
        else this.diagonalWalls.set(key, next);
        this.recalculate();
        return true;
    }

    /** Where the keyboard trace is anchored (a grid intersection), or null. Transient
     *  UI state, like a half-built antenna — never serialized. */
    getTraceAnchor(): Point | null {
        return this.traceAnchor ? { ...this.traceAnchor } : null;
    }

    setTraceAnchor(p: Point | null): void {
        this.traceAnchor = p ? { ...p } : null;
    }

    /**
     * The intersections the trace can snap to from its anchor: the four diagonal
     * neighbours whose cell can actually take a wall. The renderer marks these so the
     * next snap is visible before you steer to it.
     */
    getSnapTargets(): Point[] {
        const anchor = this.traceAnchor;
        if (!anchor) return [];

        const targets: Point[] = [];
        for (const [dr, dc] of [[-1, -1], [-1, 1], [1, -1], [1, 1]] as const) {
            const to: Point = { row: anchor.row + dr, col: anchor.col + dc };
            if (to.row < 0 || to.row > this.grid.size || to.col < 0 || to.col > this.grid.size) continue;
            const cell = diagonalCellBetween(anchor, to);
            if (cell && this.canPlaceWallAt(cell.row, cell.col)) targets.push(to);
        }
        return targets;
    }

    removeWall(row: number, col: number): void {
        this.diagonalWalls.delete(cellKey(row, col));
        this.recalculate();
    }

    getWallType(row: number, col: number): DiagonalWallType {
        return this.diagonalWalls.get(cellKey(row, col)) ?? null;
    }

    // Get fill time for a section
    getSectionFillTime(row: number, col: number, sectionId: SectionId): number | null {
        return this.sectionFillTime.get(sectionKey(row, col, sectionId)) ?? null;
    }

    // Get fill time for an entire cell (max of all its sections)
    getCellFillTime(row: number, col: number): number | null {
        const wallType = this.getWallType(row, col);
        const sections = getSections(wallType);
        let maxTime: number | null = null;

        for (const section of sections) {
            const time = this.sectionFillTime.get(sectionKey(row, col, section));
            if (time === undefined) return null;
            if (maxTime === null || time > maxTime) {
                maxTime = time;
            }
        }

        return maxTime;
    }

    // All spill points (over-an-edge drops) seen across the simulation.
    getDropHints(): DropHint[] {
        return [...this.dropHints.values()];
    }

    // Record a spill point: a section where horizontal flow tips over an edge and
    // falls. The dropping section always holds the 'd' quadrant; its 'l'/'r'
    // quadrant fixes the incoming side, and only the full-cell ('uldr') case is
    // ambiguous — there we read the section the water stepped in from.
    //
    // That has to be the *immediate* neighbour, not the upstream crash: water
    // reaches a section by whatever route the diagonals allow, and the crash is
    // regularly on the far side of one from the step the water actually took, which
    // drew the arrow arriving through a wall. Same fix as Flood's.
    protected recordDropHint(sk: string, viaKey: string | null): void {
        const { row, col, sectionId } = parseSectionKey(sk);
        const quadrants = getQuadrants(sectionId);
        const hasL = quadrants.includes('l');
        const hasR = quadrants.includes('r');

        let from: 'W' | 'E' | null = null;
        if (hasL && !hasR) {
            from = 'W';
        } else if (hasR && !hasL) {
            from = 'E';
        } else if (hasL && hasR && viaKey) {
            // A neighbour in the same cell (across a diagonal) or one row up leaves
            // no side to arrive on — that water came down, not in.
            const via = parseSectionKey(viaKey);
            if (via.row === row) {
                if (via.col < col) from = 'W';
                else if (via.col > col) from = 'E';
            }
        }

        const key = cellKey(row, col);
        if (!this.dropHints.has(key)) this.dropHints.set(key, { row, col, from });
    }

    setFaucet(col: number): void {
        if (col >= 0 && col < this.grid.size) {
            this.faucetCol = col;
            this.recalculate();
        }
    }

    // Hook for subclass to block wall placement on gate cells
    protected isGateCellAt(_row: number, _col: number): boolean {
        return false;
    }

    cycleWall(row: number, col: number): void {
        if (row < 0 || row >= this.grid.size || col < 0 || col >= this.grid.size) {
            return;
        }
        // Cannot place walls on cells containing digits
        if (this.grid.hasDigitAt({ row, col })) {
            return;
        }
        // Cannot place walls on active gate cells
        if (this.isGateCellAt(row, col)) {
            return;
        }
        const key = cellKey(row, col);
        const current = this.diagonalWalls.get(key) ?? null;
        const next = cycleWallType(current);

        if (next === null) {
            this.diagonalWalls.delete(key);
        } else {
            this.diagonalWalls.set(key, next);
        }

        this.recalculate();
    }

    protected validateWallConnectivity(): boolean {
        this.disconnectedWalls.clear();

        if (this.diagonalWalls.size === 0) return true;

        const allWallCells = new Set<string>();
        for (const key of this.diagonalWalls.keys()) {
            allWallCells.add(key);
        }

        const connected = new Set<string>();
        const queue: string[] = [];

        // Seed with walls touching boundary
        for (const [key, wallType] of this.diagonalWalls) {
            const [row, col] = key.split(',').map(Number);
            if (wallTouchesBoundary(row, col, wallType, this.grid.size)) {
                connected.add(key);
                queue.push(key);
            }
        }

        if (queue.length === 0) {
            // All walls are disconnected
            for (const key of allWallCells) this.disconnectedWalls.add(key);
            return false;
        }

        // BFS to find all connected walls
        while (queue.length > 0) {
            const current = queue.shift()!;
            const [row, col] = current.split(',').map(Number);
            const wallType = this.diagonalWalls.get(current)!;

            const neighbors = getAdjacentWallCells(row, col, wallType, this.grid.size);
            for (const n of neighbors) {
                const nKey = cellKey(n.row, n.col);
                const nWallType = this.diagonalWalls.get(nKey);
                if (nWallType && !connected.has(nKey)) {
                    // Check if they actually connect (share a corner)
                    if (wallsConnect(row, col, wallType, n.row, n.col, nWallType)) {
                        connected.add(nKey);
                        queue.push(nKey);
                    }
                }
            }
        }

        // Store disconnected walls
        for (const key of allWallCells) {
            if (!connected.has(key)) {
                this.disconnectedWalls.add(key);
            }
        }

        return connected.size === allWallCells.size;
    }

    // Flood-fill to find all reachable sections from faucet
    protected floodFillReachableSections(faucetCol: number): Set<string> {
        const reachable = new Set<string>();
        const wallType = this.getWallType(0, faucetCol);
        const startSection = getSectionContaining('u', wallType);
        const startKey = sectionKey(0, faucetCol, startSection);
        const queue: string[] = [startKey];

        const getWallTypeFn = (r: number, c: number) => this.getWallType(r, c);

        while (queue.length > 0) {
            const sk = queue.shift()!;
            if (reachable.has(sk)) continue;
            reachable.add(sk);

            const { row, col, sectionId } = parseSectionKey(sk);

            // Get all external neighbors (up, down, left, right based on quadrants)
            const neighbors = getExternalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const neighbor of neighbors) {
                if (!reachable.has(neighbor)) {
                    queue.push(neighbor);
                }
            }
        }

        return reachable;
    }

    // Tag a section if not already tagged
    private tagSection(
        sk: string,
        tag: SectionTag,
        flowState: Map<string, SectionTag>,
        toResolve: string[]
    ): void {
        if (!flowState.has(sk)) {
            flowState.set(sk, tag);
            toResolve.push(sk);
        }
    }

    // Resolve a drop section
    private resolveDrop(
        sk: string,
        flowState: Map<string, SectionTag>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: string[]
    ): void {
        const tag = flowState.get(sk);
        if (!tag || tag.type !== 'drop' || tag.resolved) return;

        const { row, col, sectionId } = parseSectionKey(sk);

        // If filled, mark resolved and return
        if (filled.has(sk)) {
            tag.resolved = true;
            return;
        }

        // If partially filled, retag as crash
        if (waterLevel.has(sk)) {
            const crashTag: SectionTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            flowState.set(sk, crashTag);
            this.resolveCrash(sk, flowState, filled, waterLevel, toResolve);
            return;
        }

        // Let T be the section below S (section in cell below containing 'u')
        // T doesn't exist if S is at grid bottom or S doesn't contain 'd' (just above a wall)
        const belowKey = sectionContains(sectionId, 'd')
            ? getNeighborSection(row, col, 'd', (r, c) => this.getWallType(r, c), this.grid.size)
            : null;

        if (belowKey === null) {
            // T doesn't exist - crash
            const crashTag: SectionTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            flowState.set(sk, crashTag);
            this.resolveCrash(sk, flowState, filled, waterLevel, toResolve);
            return;
        }

        if (filled.has(belowKey)) {
            // T is filled - crash
            const crashTag: SectionTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            flowState.set(sk, crashTag);
            this.resolveCrash(sk, flowState, filled, waterLevel, toResolve);
            return;
        }

        // T exists and is empty - drop continues
        tag.resolved = true;
        const dropTag: SectionTag = { type: 'drop', parent: sk, rate: tag.rate, resolved: false };
        this.tagSection(belowKey, dropTag, flowState, toResolve);
    }

    // Resolve a crash section
    private resolveCrash(
        sk: string,
        flowState: Map<string, SectionTag>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: string[]
    ): void {
        const tag = flowState.get(sk);
        if (!tag || tag.type !== 'crash' || tag.resolved) return;

        const { row, col, sectionId } = parseSectionKey(sk);
        tag.resolved = true;

        const getWallTypeFn = (r: number, c: number) => this.getWallType(r, c);

        // If partially filled, tag all connected non-empty sections as spread
        if (waterLevel.has(sk)) {
            const neighbors = getExternalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of neighbors) {
                if (waterLevel.has(nk) || filled.has(nk)) {
                    this.tagSection(nk, { type: 'spread', parent: sk, via: sk, resolved: false }, flowState, toResolve);
                }
            }
            return;
        }

        // Check below
        const belowKey = sectionContains(sectionId, 'd')
            ? getNeighborSection(row, col, 'd', getWallTypeFn, this.grid.size)
            : null;

        // Tag horizontal neighbors as spread
        const hNeighbors = getHorizontalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
        for (const nk of hNeighbors) {
            if (!filled.has(nk)) {
                this.tagSection(nk, { type: 'spread', parent: sk, via: sk, resolved: false }, flowState, toResolve);
            }
        }

        // If below exists and is filled, tag it as spread (to propagate through filled region)
        if (belowKey && filled.has(belowKey)) {
            this.tagSection(belowKey, { type: 'spread', parent: sk, via: sk, resolved: false }, flowState, toResolve);
        }
    }

    // Resolve a spread section
    private resolveSpread(
        sk: string,
        flowState: Map<string, SectionTag>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: string[],
        waitingDrops: string[]
    ): void {
        const tag = flowState.get(sk);
        if (!tag || tag.type !== 'spread' || tag.resolved) return;

        const { row, col, sectionId } = parseSectionKey(sk);
        const parentCrash = tag.parent;
        tag.resolved = true;

        const getWallTypeFn = (r: number, c: number) => this.getWallType(r, c);

        // If filled, tag all connected sections as spread
        if (filled.has(sk)) {
            const neighbors = getExternalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of neighbors) {
                this.tagSection(nk, { type: 'spread', parent: parentCrash, via: sk, resolved: false }, flowState, toResolve);
            }
            return;
        }

        // If partially filled, tag connected non-empty sections as spread
        if (waterLevel.has(sk)) {
            const neighbors = getExternalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of neighbors) {
                if (waterLevel.has(nk) || filled.has(nk)) {
                    this.tagSection(nk, { type: 'spread', parent: parentCrash, via: sk, resolved: false }, flowState, toResolve);
                }
            }
            return;
        }

        // Section is empty
        const belowKey = sectionContains(sectionId, 'd')
            ? getNeighborSection(row, col, 'd', getWallTypeFn, this.grid.size)
            : null;

        // Check if section can drop (has a section below)
        // Note: vertical connectivity (both u and d) is only needed for drops passing through,
        // not for spreads becoming drops - water entering from the side can still exit downward
        const canDrop = belowKey !== null;

        if (!canDrop) {
            // No section below or doesn't connect vertically - spread horizontally only
            const hNeighbors = getHorizontalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of hNeighbors) {
                if (!filled.has(nk)) {
                    this.tagSection(nk, { type: 'spread', parent: parentCrash, via: sk, resolved: false }, flowState, toResolve);
                }
            }
        } else if (filled.has(belowKey)) {
            // Below is filled - spread to below AND horizontal neighbors
            this.tagSection(belowKey, { type: 'spread', parent: parentCrash, via: sk, resolved: false }, flowState, toResolve);
            const hNeighbors = getHorizontalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of hNeighbors) {
                if (!filled.has(nk)) {
                    this.tagSection(nk, { type: 'spread', parent: parentCrash, via: sk, resolved: false }, flowState, toResolve);
                }
            }
        } else {
            // Below is empty - retag as drop (no horizontal spread). The side is the
            // step the water took to get here, not the crash it came from.
            this.recordDropHint(sk, tag.via ?? null);
            const dropTag: SectionTag = { type: 'drop', parent: parentCrash, rate: null, resolved: false };
            flowState.set(sk, dropTag);
            waitingDrops.push(sk);
        }
    }

    // Build flow state for current fill configuration
    private buildFlowState(
        faucetCol: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>
    ): { flowState: Map<string, SectionTag>; basins: Basin[] } {
        const flowState = new Map<string, SectionTag>();
        const toResolve: string[] = [];
        const waitingDrops: string[] = [];

        // Start with section below faucet
        const startWallType = this.getWallType(0, faucetCol);
        const startSection = getSectionContaining('u', startWallType);
        const startKey = sectionKey(0, faucetCol, startSection);
        const startTag: SectionTag = { type: 'drop', parent: null, rate: { num: 1, denom: 1 }, resolved: false };
        flowState.set(startKey, startTag);
        toResolve.push(startKey);

        // Resolve all sections
        while (toResolve.length > 0) {
            const sk = toResolve.shift()!;
            const tag = flowState.get(sk);
            if (!tag || tag.resolved) continue;

            if (tag.type === 'drop') {
                this.resolveDrop(sk, flowState, filled, waterLevel, toResolve);
            } else if (tag.type === 'crash') {
                this.resolveCrash(sk, flowState, filled, waterLevel, toResolve);
            } else if (tag.type === 'spread') {
                this.resolveSpread(sk, flowState, filled, waterLevel, toResolve, waitingDrops);
            }
        }

        // Process waiting drops iteratively
        while (waitingDrops.length > 0) {
            // Group by parent crash
            const byParent = new Map<string, string[]>();
            const currentWaiting = [...waitingDrops];
            waitingDrops.length = 0;

            for (const dk of currentWaiting) {
                const tag = flowState.get(dk) as SectionTag & { type: 'drop' };
                const parent = tag.parent!;
                if (!byParent.has(parent)) byParent.set(parent, []);
                byParent.get(parent)!.push(dk);
            }

            // Assign rates and resolve
            for (const [parentKey, children] of byParent) {
                const parentTag = flowState.get(parentKey);
                if (!parentTag || parentTag.type !== 'crash') continue;

                const parentRate = parentTag.rate;
                const childRate = divideRational(parentRate, children.length);

                for (const dk of children) {
                    const tag = flowState.get(dk) as SectionTag & { type: 'drop' };
                    tag.rate = childRate;
                    toResolve.push(dk);
                }
            }

            // Resolve the waiting drops
            while (toResolve.length > 0) {
                const sk = toResolve.shift()!;
                const tag = flowState.get(sk);
                if (!tag || tag.resolved) continue;

                if (tag.type === 'drop') {
                    this.resolveDrop(sk, flowState, filled, waterLevel, toResolve);
                } else if (tag.type === 'crash') {
                    this.resolveCrash(sk, flowState, filled, waterLevel, toResolve);
                } else if (tag.type === 'spread') {
                    this.resolveSpread(sk, flowState, filled, waterLevel, toResolve, waitingDrops);
                }
            }
        }

        // Find terminal crashes
        const dropParents = new Set<string>();
        for (const [, tag] of flowState) {
            if (tag.type === 'drop' && tag.parent) {
                dropParents.add(tag.parent);
            }
        }

        const basins: Basin[] = [];
        for (const [sk, tag] of flowState) {
            if (tag.type === 'crash' && !dropParents.has(sk)) {
                // Terminal crash - collect basin sections
                const basinSections: string[] = [sk];
                for (const [ssk, stag] of flowState) {
                    if (stag.type === 'spread' && stag.parent === sk && !filled.has(ssk)) {
                        basinSections.push(ssk);
                    }
                }
                basins.push({ sections: basinSections, rate: tag.rate });
            }
        }

        return { flowState, basins };
    }

    protected recalculate(): void {
        this.simulate();
    }

    /**
     * Flood-fill the sections the water can actually get to, and flag the board when
     * some can't be reached — keeping {@link getReachableSections} in step so the
     * renderer can shade what *is* reachable. Purely structural: it reads the walls,
     * not the flow, so it holds even on a board whose walls are disconnected.
     */
    protected updateReachability(): void {
        const reachable = this.reachableFrom(this.faucetCol);

        let expectedSections = 0;
        for (let row = 0; row < this.grid.size; row++) {
            for (let col = 0; col < this.grid.size; col++) {
                expectedSections += getSections(this.getWallType(row, col)).length;
            }
        }

        this.hasUnreachableSections = reachable.size < expectedSections;
        this.reachableSections = this.hasUnreachableSections ? reachable : new Set();
    }

    /** The flood-fill reachability rests on; {@link DelugeVortex} follows gates too. */
    protected reachableFrom(faucetCol: number): Set<string> {
        return this.floodFillReachableSections(faucetCol);
    }

    // Export walls in shortform
    protected exportWalls(): string {
        const walls: string[] = [];
        for (const [key, wallType] of this.diagonalWalls) {
            const [row, col] = key.split(',').map(Number);
            const letter = colToLetter(col);
            walls.push(`${letter}${row}${wallType}`);
        }
        return walls.join(', ');
    }

    protected simulate(): void {
        this.sectionFillTime.clear();
        this.reachableSections.clear();
        this.dropHints.clear();
        this.debug.clear();

        // Work out reachability first. It's a plain structural flood-fill that owes
        // nothing to the flow simulation, and a board can be walled off *and*
        // disconnected at once — so deciding it up front is what lets the walled-off
        // area still be reported and shaded when the walls are also disconnected.
        this.updateReachability();

        if (!this.validateWallConnectivity()) return;
        if (this.hasUnreachableSections) return; // flagged and shaded; timings are moot

        const filled = new Set<string>();
        const waterLevel = new Map<string, Rational>();
        let currentTime: Rational = { num: 0, denom: 1 };
        let debugTimestampIndex = 0;

        // Total sections in grid
        let totalSections = 0;
        for (let row = 0; row < this.grid.size; row++) {
            for (let col = 0; col < this.grid.size; col++) {
                const wallType = this.getWallType(row, col);
                totalSections += getSections(wallType).length;
            }
        }

        const maxIterations = totalSections * 2;
        let iterations = 0;

        while (iterations++ < maxIterations && rationalToNumber(currentTime) < 400) {
            const { flowState, basins } = this.buildFlowState(this.faucetCol, filled, waterLevel);

            // Capture snapshots at debug timestamps
            debugTimestampIndex = this.debug.maybeCaptureSnapshot(
                rationalToNumber(currentTime),
                debugTimestampIndex,
                filled,
                waterLevel,
                flowState,
                basins
            );

            if (basins.length === 0) {
                break;
            }

            // Calculate fill time for each basin
            // Each section fills at rate proportional to its size (number of quadrants)
            const basinFillTimes: Array<{ basin: Basin; fillTime: Rational; perQuadrantRate: Rational }> = [];

            for (const basin of basins) {
                // Total quadrants in this basin
                let totalQuadrants = 0;
                for (const sk of basin.sections) {
                    const { sectionId } = parseSectionKey(sk);
                    totalQuadrants += getSectionSize(sectionId);
                }

                const perQuadrantRate = divideRational(basin.rate, totalQuadrants);

                // Use first section to determine fill level (all should be same)
                const sampleSection = basin.sections[0];
                const currentLevel = waterLevel.get(sampleSection) ?? { num: 0, denom: 1 };
                const remaining = subtractRationals({ num: 1, denom: 1 }, currentLevel);

                if (compareRationals(remaining, { num: 0, denom: 1 }) <= 0) {
                    continue;
                }

                // Time to fill = remaining / perQuadrantRate
                // All quadrants in basin fill simultaneously, so section fill rate = perQuadrantRate
                const timeToFill: Rational = reduceRational({
                    num: remaining.num * perQuadrantRate.denom,
                    denom: remaining.denom * perQuadrantRate.num
                });
                const fillTime = addRationals(currentTime, timeToFill);

                basinFillTimes.push({ basin, fillTime, perQuadrantRate });
            }

            if (basinFillTimes.length === 0) {
                break;
            }

            // Find earliest fill time
            let earliestTime = basinFillTimes[0].fillTime;
            for (const { fillTime } of basinFillTimes) {
                if (compareRationals(fillTime, earliestTime) < 0) {
                    earliestTime = fillTime;
                }
            }

            // Advance time and update water levels for ALL basins
            // All quadrants fill at perQuadrantRate, so section fill fraction increases at perQuadrantRate
            const elapsed = subtractRationals(earliestTime, currentTime);
            for (const { basin, perQuadrantRate } of basinFillTimes) {
                for (const sk of basin.sections) {
                    const added = multiplyRationals(elapsed, perQuadrantRate);
                    const current = waterLevel.get(sk) ?? { num: 0, denom: 1 };
                    waterLevel.set(sk, addRationals(current, added));
                }
            }

            // Mark ALL basins that complete at the earliest time
            const fillTimeNum = Math.round(rationalToNumber(earliestTime) * 10) / 10;
            for (const { basin, fillTime } of basinFillTimes) {
                if (compareRationals(fillTime, earliestTime) === 0) {
                    for (const sk of basin.sections) {
                        this.sectionFillTime.set(sk, fillTimeNum);
                        filled.add(sk);
                    }
                }
            }

            currentTime = earliestTime;
        }

        // Check if flow algorithm filled all sections
        const filledCount = this.sectionFillTime.size;
        if (filledCount < totalSections) {
            return;
        }
    }

    checkWin(): boolean {
        let previousTime = -1;
        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.digitPositions.get(digit);
            if (!pos) return false;

            const time = this.getCellFillTime(pos.row, pos.col);
            if (time === null) return false;
            if (time <= previousTime) return false;

            previousTime = time;
        }
        return true;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        // A = diagonal wall count (cross = 2, single diagonal = 1)
        let wallCount = 0;
        for (const [, wallType] of this.diagonalWalls) {
            if (wallType === 'x') {
                wallCount += 2;
            } else {
                wallCount += 1;
            }
        }

        // B = time spread (last digit time - first digit time)
        const posMin = this.digitPositions.get(this.grid.minDigit);
        const posMax = this.digitPositions.get(this.grid.maxDigit);
        if (!posMin || !posMax) return null;

        const timeMin = this.getCellFillTime(posMin.row, posMin.col);
        const timeMax = this.getCellFillTime(posMax.row, posMax.col);
        if (timeMin === null || timeMax === null) return null;

        const timeSpread = Math.round(timeMax - timeMin);

        return calculateScore(wallCount, timeSpread);
    }

    /** Scoring-rules overlay: time spread (secondary) as an arc between the min/max
     *  digit. Diagonal walls (primary) are redrawn in amber by the renderer. */
    getScoringHighlight(): ScoringHighlight {
        const from = this.grid.getDigitPosition(this.grid.minDigit);
        const to = this.grid.getDigitPosition(this.grid.maxDigit);
        if (!from || !to) return {};
        const fromValue = Math.round(this.getCellFillTime(from.row, from.col) ?? 0);
        const toValue = Math.round(this.getCellFillTime(to.row, to.col) ?? 0);
        return { spreadArc: { from, to, fromValue, toValue, primary: false, badgeSize: 'lg' } };
    }

    getCorrectDigits(): number[] {
        const correct: number[] = [];
        let previousTime = -1;

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.digitPositions.get(digit);
            if (!pos) break;

            const time = this.getCellFillTime(pos.row, pos.col);
            if (time === null) break;
            if (time <= previousTime) break;

            correct.push(digit);
            previousTime = time;
        }
        return correct;
    }

    /**
     * The first pair of digits that fill out of order: `before` should fill
     * before `after`, but currently fills at the same time or later. Returns null
     * if every filled digit is in order (a later digit simply not filled yet is
     * not a violation).
     */
    getFirstFillOrderViolation(): { before: number; after: number } | null {
        let previousTime = -1;
        let previousDigit = -1;

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.digitPositions.get(digit);
            if (!pos) break;

            const time = this.getCellFillTime(pos.row, pos.col);
            if (time === null) break;  // not filled yet — not an order violation
            if (time <= previousTime) return { before: previousDigit, after: digit };

            previousTime = time;
            previousDigit = digit;
        }
        return null;
    }

    isStarted(): boolean {
        return this.faucetCol !== 0 || this.diagonalWalls.size > 0;
    }

    reset(): void {
        this.faucetCol = 0;
        this.diagonalWalls.clear();
        this.sectionFillTime.clear();
        this.reachableSections.clear();
        this.dropHints.clear();
        this.disconnectedWalls.clear();
        this.hasUnreachableSections = false;
        this.recalculate();
    }

    /**
     * Jumpstart a solution: cup the first digit with diagonal walls (its own cell
     * can't hold a wall) and put the faucet straight above it, so it fills before
     * digit 2. Near an edge the cup self-anchors with a couple of walls; when the
     * digit is interior a zigzag "pole" of alternating walls snakes down around
     * other digits to a rim. All candidate cups (+ optional pole) are verified
     * against the flow simulator; the fewest-wall one that works is kept.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const min = this.grid.minDigit;
        const d1 = this.digitPositions.get(min);
        const d2 = this.digitPositions.get(min + 1);
        if (!d1 || !d2) return false;

        const size = this.grid.size;
        const r1 = d1.row, c1 = d1.col;
        const free = (r: number, c: number) =>
            r >= 0 && r < size && c >= 0 && c < size && !this.grid.hasDigitAt({ row: r, col: c });

        // Routed anchor pole, from just below the cup (with/without a wall below d1).
        const poleBelow = r1 + 2 < size ? this.routedPole(r1 + 2, c1) : null;
        const poleDirect = r1 + 1 < size ? this.routedPole(r1 + 1, c1) : null;

        const opts: DiagonalWallType[] = [null, 'f', 'b', 'x'];
        const candidates: Array<Array<[number, number, DiagonalWallType]>> = [];
        for (const below of opts) {
            for (const left of opts) {
                for (const right of opts) {
                    for (const withPole of [false, true]) {
                        const walls: Array<[number, number, DiagonalWallType]> = [];
                        const used = new Set<string>();
                        const add = (r: number, c: number, t: DiagonalWallType) => {
                            if (t && free(r, c) && !used.has(cellKey(r, c))) {
                                walls.push([r, c, t]);
                                used.add(cellKey(r, c));
                            }
                        };
                        add(r1 + 1, c1, below);
                        add(r1, c1 - 1, left);
                        add(r1, c1 + 1, right);
                        if (withPole) {
                            const pole = below ? poleBelow : poleDirect;
                            if (!pole) continue;
                            for (const [r, c, t] of pole) add(r, c, t);
                        }
                        if (walls.length > 0) candidates.push(walls);
                    }
                }
            }
        }
        // Prefer the fewest walls for a tidy jumpstart.
        candidates.sort((a, b) => a.length - b.length);

        for (const walls of candidates) {
            this.diagonalWalls.clear();
            for (const [r, c, t] of walls) this.diagonalWalls.set(cellKey(r, c), t!);
            this.faucetCol = c1;
            this.recalculate();
            if (this.fillsFirstDigitBeforeSecond(d1, d2)) return true;
        }

        this.reset();
        return false;
    }

    /** True if the grid fully fills and digit 1's cell fills strictly before digit 2's. */
    private fillsFirstDigitBeforeSecond(d1: Position, d2: Position): boolean {
        if (this.hasUnreachableSections) return false;

        let totalSections = 0;
        for (let r = 0; r < this.grid.size; r++) {
            for (let c = 0; c < this.grid.size; c++) {
                totalSections += getSections(this.getWallType(r, c)).length;
            }
        }
        if (this.sectionFillTime.size !== totalSections) return false;

        const t1 = this.getCellFillTime(d1.row, d1.col);
        const t2 = this.getCellFillTime(d2.row, d2.col);
        return t1 !== null && t2 !== null && t1 < t2;
    }

    /**
     * BFS a connected zigzag pole of diagonal walls downward from (startRow, col),
     * dodging digit cells, until a wall touches a boundary (anchored). Consecutive
     * walls connect at a shared corner: f → b below (straight) or f below-left;
     * b → f below (straight) or b below-right. Null if no anchored route exists.
     */
    private routedPole(startRow: number, col: number): Array<[number, number, DiagonalWallType]> | null {
        const size = this.grid.size;
        const free = (r: number, c: number) =>
            r >= 0 && r < size && c >= 0 && c < size && !this.grid.hasDigitAt({ row: r, col: c });

        interface S { r: number; c: number; t: 'f' | 'b'; }
        const key = (s: S) => `${s.r},${s.c},${s.t}`;
        const parent = new Map<string, S | null>();
        const queue: S[] = [];
        for (const t of ['f', 'b'] as Array<'f' | 'b'>) {
            if (free(startRow, col)) {
                const s: S = { r: startRow, c: col, t };
                parent.set(key(s), null);
                queue.push(s);
            }
        }

        while (queue.length > 0) {
            const s = queue.shift()!;
            if (wallTouchesBoundary(s.r, s.c, s.t, size)) {
                const walls: Array<[number, number, DiagonalWallType]> = [];
                let cur: S | null = s;
                while (cur) { walls.push([cur.r, cur.c, cur.t]); cur = parent.get(key(cur)) ?? null; }
                return walls.reverse();
            }
            const downs: S[] = s.t === 'f'
                ? [{ r: s.r + 1, c: s.c, t: 'b' }, { r: s.r + 1, c: s.c - 1, t: 'f' }]
                : [{ r: s.r + 1, c: s.c, t: 'f' }, { r: s.r + 1, c: s.c + 1, t: 'b' }];
            for (const n of downs) {
                if (!free(n.r, n.c) || parent.has(key(n))) continue;
                parent.set(key(n), s);
                queue.push(n);
            }
        }
        return null;
    }

    getStatus(): string {
        const boldRed = (t: string) => `<span style="color:#e94560;font-weight:bold">${t}</span>`;

        if (this.checkWin()) {
            return 'You win! All digits filled in order!';
        }

        // The only real error states: regions the water can never get to, and walls
        // that don't reach the rim. Out-of-order fills are not an error — on most
        // boards the digits start out of order, so the default hint below just steers
        // you.
        //
        // Walling a section off comes first: it's the more concrete break, and a wall
        // that seals a section is usually disconnected as well, so naming only the
        // connectivity problem would point at the symptom rather than the cause.
        if (this.hasUnreachableSections) {
            return `${boldRed('Invalid')}: some areas are walled off and cannot be reached.`;
        }

        if (!this.validateWallConnectivity()) {
            return `${boldRed('Invalid')}: walls must connect to the grid edge.`;
        }

        const [before, after] = this.getFillPairToEstablish();
        return `Click cells to place walls and fill ${before} before ${after}. ` +
            `Click top edges to move the faucet.`;
    }

    /**
     * The two digits the player is currently trying to order: the boundary of the
     * correct run and the next digit after it — the pair "fill X before Y" names.
     * Falls back to the first two digits before any are correctly in order.
     */
    getFillPairToEstablish(): [number, number] {
        const frontier = Math.max(1, this.getCorrectDigits().length);
        return [this.grid.minDigit + frontier - 1, this.grid.minDigit + frontier];
    }

    /** Remove fill times beyond maxTime (for tutorial progressive display). */
    limitDisplayTime(maxTime: number): void {
        for (const [key, time] of this.sectionFillTime) {
            if (time > maxTime) {
                this.sectionFillTime.delete(key);
            }
        }
    }

    /**
     * Serialize game state to string format.
     * Format: "D,A3fB8xI4b" - faucet letter, then wall positions with types
     */
    serialize(): string {
        const faucetLetter = String.fromCharCode('A'.charCodeAt(0) + this.faucetCol);
        const wallParts: string[] = [];

        for (const [key, wallType] of this.diagonalWalls) {
            const [row, col] = key.split(',').map(Number);
            const letter = String.fromCharCode('A'.charCodeAt(0) + col);
            wallParts.push(`${letter}${row}${wallType}`);
        }

        if (wallParts.length === 0) {
            return faucetLetter;
        }
        return faucetLetter + wallParts.join('');
    }

    /**
     * Deserialize game state from string format.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        const compact = stripSeparators(serialized);
        if (!compact) return;

        // First char is the faucet column letter.
        const faucetLetter = compact[0];
        if (faucetLetter >= 'A' && faucetLetter <= 'J') {
            this.faucetCol = faucetLetter.charCodeAt(0) - 'A'.charCodeAt(0);
        }

        // The rest is concatenated 3-char wall entries.
        const wallStr = compact.slice(1);
        const regex = /([A-J])(\d)(f|b|x)/g;
        let match;
        while ((match = regex.exec(wallStr)) !== null) {
            const col = match[1].charCodeAt(0) - 'A'.charCodeAt(0);
            const row = parseInt(match[2], 10);
            const wallType = match[3] as DiagonalWallType;

            if (!this.grid.hasDigitAt({ row, col })) {
                this.diagonalWalls.set(cellKey(row, col), wallType);
            }
        }

        this.recalculate();
    }
}
